import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import { apiRequest } from '../../utils/api';
import backIcon from '../../assets/icons/common/back_icon.png';

const TableQrManager = ({ isOpen, onClose, onScannerOpenChange, closeScannerRef }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [tables, setTables] = useState([]);
  const [store, setStore] = useState(null);

  // 토스트 메시지
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

  // 확인 다이얼로그
  const [confirmDialog, setConfirmDialog] = useState({ show: false, qrCode: '', tableName: '' });

  // 카메라 스캐너
  const [scannerOpen, setScannerOpen] = useState(false);
  const [selectedTable, setSelectedTable] = useState(null);
  const html5QrCodeRef = useRef(null);
  const scannerOpenRef = useRef(false);

  // 카메라 권한 거부 안내
  const [permissionDenied, setPermissionDenied] = useState(false);

  // UI 뒤로가기 버튼 클릭 시
  const handleBackClick = useCallback(() => {
    onClose?.();
  }, [onClose]);

  // 테이블 목록 조회
  const fetchTables = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await apiRequest('/qr-link/tables');
      if (response.success) {
        setStore(response.store);
        setTables(response.tables);
      }
    } catch (error) {
      console.error('Failed to fetch tables:', error);
      showToast('테이블 목록을 불러오는데 실패했습니다', 'error');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const toastTimerRef = useRef(null);

  // 토스트 메시지 표시
  const showToast = (message, type = 'success') => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    setToast({ show: false, message: '', type: 'success' });
    setTimeout(() => {
      setToast({ show: true, message, type });
      toastTimerRef.current = setTimeout(() => {
        setToast({ show: false, message: '', type: 'success' });
      }, 2000);
    }, 10);
  };

  // 애니메이션 처리
  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
      fetchTables();
      setTimeout(() => {
        setIsAnimating(true);
      }, 10);
    } else {
      setIsAnimating(false);
      const timer = setTimeout(() => {
        setIsVisible(false);
      }, 150);
      return () => clearTimeout(timer);
    }
  }, [isOpen, fetchTables]);

  // body 스크롤 방지
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // 매장설정 페이지와 동일한 zoom 스케일링
  useEffect(() => {
    const scaleUI = () => {
      const wrapper = document.querySelector('.manager-wrapper');
      if (wrapper) {
        const viewportWidth = window.innerWidth;
        const scale = Math.min(viewportWidth / 360, 1.5);
        wrapper.style.zoom = scale;
      }
    };

    if (isVisible) {
      scaleUI();
      window.addEventListener('resize', scaleUI);
    }

    return () => {
      window.removeEventListener('resize', scaleUI);
    };
  }, [isVisible]);

  // QR 촬영 버튼 클릭 → 카메라 스캐너 열기
  const handleQrScanClick = (table) => {
    setSelectedTable(table);
    setScannerOpen(true);
    scannerOpenRef.current = true;
    onScannerOpenChange?.(true);
    // 스캐너는 뒤로가기로 닫지 않음 (UI 닫기 버튼만 사용)
  };

  // 카메라 스캐너 시작
  useEffect(() => {
    if (!scannerOpen) return;

    let scanner = null;
    let stopped = false;

    const startScanner = async () => {
      try {
        scanner = new Html5Qrcode('qr-reader');
        html5QrCodeRef.current = scanner;

        const videoConstraints = {
          facingMode: 'environment',
          width: { min: 640, ideal: 1920 },
          height: { min: 480, ideal: 1080 }
        };
        const stream = await navigator.mediaDevices.getUserMedia({ video: videoConstraints });
        const track = stream.getVideoTracks()[0];
        const cameraId = track.getSettings().deviceId;
        stream.getTracks().forEach(t => t.stop());

        await scanner.start(
          cameraId,
          {
            fps: 15,
            qrbox: { width: 200, height: 200 }
          },
          (decodedText) => {
            if (stopped) return;
            stopped = true;
            handleQrDetected(decodedText);
          },
          () => {}
        );
      } catch (err) {
        console.error('[QR] Camera start error:', err);
        if (!stopped) {
          setScannerOpen(false);
          scannerOpenRef.current = false;
          setSelectedTable(null);
          onScannerOpenChange?.(false);
          if (err && (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError')) {
            setPermissionDenied(true);
          } else {
            showToast('카메라를 열 수 없습니다.', 'error');
          }
        }
      }
    };

    // DOM에 #qr-reader가 렌더된 후 시작
    const timer = setTimeout(startScanner, 100);

    return () => {
      stopped = true;
      clearTimeout(timer);
      if (scanner && scanner.isScanning) {
        scanner.stop().catch(() => {});
      }
      html5QrCodeRef.current = null;
    };
  }, [scannerOpen]);

  // 카메라 스캐너 닫기
  const closeScanner = useCallback(async () => {
    const scanner = html5QrCodeRef.current;
    if (scanner && scanner.isScanning) {
      try {
        await scanner.stop();
      } catch (err) {
        console.log('[QR] Scanner stop error (ignored):', err);
      }
    }
    setScannerOpen(false);
    scannerOpenRef.current = false;
    setSelectedTable(null);
    onScannerOpenChange?.(false);
  }, [onScannerOpenChange]);

  // 부모에서 closeScanner 호출할 수 있도록 ref에 할당
  useEffect(() => {
    if (closeScannerRef) {
      closeScannerRef.current = closeScanner;
    }
  }, [closeScannerRef, closeScanner]);

  // QR 인식 완료 처리
  const handleQrDetected = async (decodedText) => {
    // 스캐너 먼저 닫기
    const scanner = html5QrCodeRef.current;
    if (scanner && scanner.isScanning) {
      await scanner.stop().catch(() => {});
    }
    setScannerOpen(false);
    scannerOpenRef.current = false;
    onScannerOpenChange?.(false);

    try {
      const parsed = parseScannedQr(decodedText);
      console.log('[QR] Detected:', decodedText, '→ parsed:', parsed);

      if (!parsed) {
        showToast(`유효하지 않은 QR 형식입니다`, 'error');
        setSelectedTable(null);
        return;
      }

      // 연결 API 호출 — naver_me_url 또는 qr_code 둘 중 하나로 매칭
      const body = {
        table_cd: selectedTable.table_cd,
        table_nm: selectedTable.table_nm,
      };
      if (parsed.naver_me_url) body.naver_me_url = parsed.naver_me_url;
      if (parsed.qr_code) body.qr_code = parsed.qr_code;

      const connectResponse = await apiRequest('/qr-link/connect-now', {
        method: 'POST',
        body: JSON.stringify(body),
      });

      if (connectResponse.success) {
        showToast('QR이 연결됐어요!', 'success');
        fetchTables();
      } else {
        showToast(connectResponse.error || '연결에 실패했습니다', 'error');
      }
    } catch (error) {
      console.error('QR process error:', error);
      showToast('QR 연동 권한이 없습니다', 'error');
    } finally {
      setSelectedTable(null);
    }
  };

  // 스캔한 URL/텍스트에서 식별자 추출
  // 우선순위: naver.me > /order/{code} > 6~12자리 plain code
  const parseScannedQr = (raw) => {
    console.log('[QR] Raw QR data:', raw);
    if (!raw) return null;
    const text = raw.trim();

    // 1) naver.me 단축 URL (신규 출고 QR)
    const naverMatch = text.match(/(?:https?:\/\/)?naver\.me\/[A-Za-z0-9_-]+/i);
    if (naverMatch) {
      let url = naverMatch[0];
      if (!/^https?:\/\//i.test(url)) url = 'https://' + url;
      return { naver_me_url: url };
    }

    // 2) /order/XXXXXXXX 형식 (레거시)
    const orderMatch = text.match(/\/order\/([A-Za-z0-9]+)/);
    if (orderMatch) return { qr_code: orderMatch[1] };

    // 3) URL 끝부분의 path 8자리 정도
    const urlMatch = text.match(/\/([A-Za-z0-9]{6,12})(?:\?.*)?$/);
    if (urlMatch) return { qr_code: urlMatch[1] };

    // 4) URL이 아닌 경우 그대로 반환 (6~12자리 영숫자)
    if (/^[A-Za-z0-9]{6,12}$/.test(text)) return { qr_code: text };

    return null;
  };

  // QR 해지 클릭
  const handleDisconnectClick = (qrCode, tableName) => {
    setConfirmDialog({ show: true, qrCode, tableName });
  };

  // 해지 확인 - 즉시 API 호출
  const confirmDisconnect = async () => {
    const { qrCode } = confirmDialog;
    setConfirmDialog({ show: false, qrCode: '', tableName: '' });

    try {
      const response = await apiRequest('/qr-link/disconnect-now', {
        method: 'POST',
        body: JSON.stringify({ qr_code: qrCode })
      });

      if (response.success) {
        showToast('QR이 해제됐어요!', 'success');
        fetchTables();
      } else {
        showToast(response.error || '해제에 실패했습니다', 'error');
      }
    } catch (error) {
      console.error('Disconnect error:', error);
      showToast('해제 중 오류가 발생했습니다', 'error');
    }
  };

  // 테이블별 QR 목록
  const getTableQrList = (table) => {
    return (table.qr_codes || []);
  };

  // 연결된 QR 총 개수
  const totalLinkedQrs = tables.reduce((count, table) => {
    const qrs = table.qr_codes || [];
    return count + qrs.length;
  }, 0);

  if (!isVisible) return null;

  return (
    <>
      <style jsx="true">{`
        .table-qr-manager {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: white;
          z-index: 10001;
          transform: translateX(100%);
          transition: transform 0.15s cubic-bezier(0.4, 0.0, 0.2, 1);
          overflow: hidden;
          display: flex;
          flex-direction: column;
        }

        .table-qr-manager.open {
          transform: translateX(0);
        }

        .manager-wrapper {
          width: 360px;
          height: 100vh;
          height: 100dvh;
          margin: 0 auto;
          position: relative;
          display: flex;
          flex-direction: column;
          transform-origin: top center;
          overflow: hidden;
        }

        .manager-header {
          width: 100%;
          height: 55px;
          background-color: #ffffff;
          border-bottom: 1px solid #E8E8E8;
          display: flex;
          align-items: center;
          justify-content: center;
          position: relative;
          flex-shrink: 0;
        }

        .back-button {
          position: absolute;
          left: 18px;
          width: 27px;
          height: 27px;
          cursor: pointer;
        }

        .header-title {
          font-size: 18px;
          font-weight: bold;
          color: #000000;
        }

        .manager-content {
          flex: 1;
          overflow-y: auto;
          -webkit-overflow-scrolling: touch;
          padding-bottom: 20px;
        }

        .summary-bar {
          padding: 16px 22px;
          background: #FAFAFB;
          border-bottom: 1px solid #E8E8E8;
          font-size: 14px;
          color: #666;
        }
        .linked-qr-count {
          color: #2ECC71;
          font-weight: 700;
        }

        .table-list {
          padding: 16px 22px;
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .table-card {
          background: #FFFFFF;
          border: 1px solid #E8E8E8;
          border-radius: 12px;
          padding: 16px;
        }

        .table-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 12px;
          padding-bottom: 12px;
          border-bottom: 1px solid #F0F0F0;
        }

        .table-name {
          font-size: 18px;
          font-weight: 600;
          color: #000;
        }

        .table-code {
          font-size: 14px;
          color: #999;
        }

        .qr-list {
          display: flex;
          flex-direction: column;
          gap: 8px;
          margin-bottom: 12px;
        }

        .qr-item {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 10px 12px;
          background: #F9F9F9;
          border-radius: 8px;
        }

        .qr-url {
          font-size: 11px;
          font-family: 'Pretendard', -apple-system, BlinkMacSystemFont, sans-serif;
          word-break: break-all;
          flex: 1;
          margin-right: 10px;
        }

        .qr-url.existing {
          color: #999999;
          text-decoration: none;
        }

        .qr-url.existing:active {
          color: #666666;
        }

        .qr-action-btn {
          background: none;
          border: none;
          padding: 4px 8px;
          cursor: pointer;
          font-size: 11px;
          font-weight: 500;
          font-family: 'Pretendard', -apple-system, BlinkMacSystemFont, sans-serif;
          white-space: nowrap;
          border-radius: 4px;
        }

        .qr-action-btn.disconnect {
          color: #F44336;
        }

        .qr-action-btn.disconnect:active {
          background: #FFEBEE;
        }

        .no-qr-text {
          font-size: 13px;
          color: #999;
          margin-bottom: 12px;
        }

        .scan-button {
          width: 100%;
          padding: 12px;
          border: 2px dashed #CCCCCC;
          border-radius: 8px;
          background: transparent;
          color: #666;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
        }

        /* 로딩 */
        .loading-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          height: 200px;
          gap: 16px;
        }

        .loading-spinner {
          width: 32px;
          height: 32px;
          border: 3px solid #E8E8E8;
          border-top-color: #FDCD27;
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        /* 토스트 */
        .toast-popup {
          position: fixed;
          bottom: 80px;
          left: 50%;
          transform: translateX(-50%);
          background-color: #3C3C3C;
          color: #ffffff;
          padding: 0 16px;
          height: 42px;
          display: flex;
          align-items: center;
          border-radius: 100px;
          font-size: 14px;
          font-weight: 600;
          z-index: 10002;
          white-space: nowrap;
          animation: toastSlideUp 0.3s ease-out, toastSlideDown 0.3s ease-out 1.7s forwards;
        }


        @keyframes toastSlideUp {
          from { transform: translateX(-50%) translateY(20px); opacity: 0; }
          to { transform: translateX(-50%) translateY(0); opacity: 1; }
        }

        @keyframes toastSlideDown {
          from { transform: translateX(-50%) translateY(0); opacity: 1; }
          to { transform: translateX(-50%) translateY(20px); opacity: 0; }
        }

        /* 확인 다이얼로그 */
        .dialog-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 10003;
        }

        .dialog-content {
          background: white;
          border-radius: 16px;
          padding: 24px;
          width: 280px;
          text-align: center;
        }

        .dialog-title {
          font-size: 18px;
          font-weight: 600;
          margin-bottom: 16px;
        }

        .dialog-qr {
          display: inline-block;
          padding: 8px 16px;
          background: #FFF8E1;
          border: 1px solid #FDCD27;
          border-radius: 6px;
          font-size: 14px;
          font-weight: 500;
          margin-bottom: 12px;
        }

        .dialog-desc {
          font-size: 13px;
          color: #666;
          margin-bottom: 20px;
        }

        .dialog-buttons {
          display: flex;
          gap: 12px;
        }

        .dialog-btn {
          flex: 1;
          padding: 12px;
          border-radius: 8px;
          font-size: 15px;
          font-weight: 600;
          cursor: pointer;
          border: none;
        }

        .dialog-btn.cancel {
          background: #F5F5F5;
          color: #333;
        }

        .dialog-btn.confirm {
          background: #F44336;
          color: white;
        }

        /* 카메라 스캐너 모달 - 전체화면 */
        .scanner-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: #000;
          z-index: 10010;
          display: flex;
          flex-direction: column;
        }

        .scanner-header {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 55px;
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 10012;
          background: linear-gradient(to bottom, rgba(0,0,0,0.6), transparent);
        }

        .scanner-title {
          font-size: 17px;
          font-weight: 600;
          color: #fff;
        }

        .scanner-close-btn {
          position: absolute;
          right: 16px;
          top: 50%;
          transform: translateY(-50%);
          background: none;
          border: none;
          color: #fff;
          font-size: 16px;
          font-weight: 500;
          cursor: pointer;
          padding: 8px 12px;
        }

        .scanner-body {
          width: 100%;
          height: 100%;
          position: relative;
          overflow: hidden;
        }

        #qr-reader {
          width: 100% !important;
          height: 100% !important;
          border: none !important;
          overflow: hidden;
        }

        #qr-reader video {
          width: 100% !important;
          height: 100% !important;
          object-fit: cover !important;
        }

        /* html5-qrcode 내부 UI 숨기기 */
        #qr-reader__scan_region {
          min-height: 0 !important;
        }
        #qr-reader__scan_region > br {
          display: none !important;
        }
        #qr-reader__dashboard {
          display: none !important;
        }
        #qr-reader__status_span {
          display: none !important;
        }
        #qr-reader__header_message {
          display: none !important;
        }
        #qr-reader img[alt="Info icon"],
        #qr-reader img[alt="Camera based scan"] {
          display: none !important;
        }

        .scanner-bottom {
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          padding-bottom: 60px;
          display: flex;
          flex-direction: column;
          align-items: center;
          z-index: 10012;
          background: linear-gradient(to top, rgba(0,0,0,0.6), transparent);
        }

        .scanner-guide {
          text-align: center;
          color: rgba(255, 255, 255, 0.85);
          font-size: 15px;
        }

        .scanner-table-info {
          margin-top: 12px;
          padding: 8px 20px;
          background: rgba(253, 205, 39, 0.9);
          border-radius: 20px;
          color: #333;
          font-size: 14px;
          font-weight: 600;
        }

        /* 카메라 권한 거부 안내 */
        .permission-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 10020;
        }

        .permission-content {
          background: white;
          border-radius: 16px;
          padding: 28px 24px;
          width: 300px;
          text-align: center;
        }

        .permission-icon {
          width: 56px;
          height: 56px;
          background: #FFF3E0;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 16px;
        }

        .permission-title {
          font-size: 18px;
          font-weight: 700;
          color: #000;
          margin-bottom: 12px;
        }

        .permission-desc {
          font-size: 14px;
          color: #555;
          line-height: 1.6;
          margin-bottom: 8px;
        }

        .permission-steps {
          text-align: left;
          background: #F7F7F8;
          border-radius: 10px;
          padding: 14px 16px;
          margin-bottom: 20px;
        }

        .permission-step {
          font-size: 13px;
          color: #333;
          line-height: 1.8;
        }

        .permission-step strong {
          color: #000;
        }

        .permission-btn {
          width: 100%;
          padding: 14px;
          border: none;
          border-radius: 10px;
          font-size: 16px;
          font-weight: 600;
          cursor: pointer;
          background: #FDCD27;
          color: #fff;
        }

        .permission-btn:active {
          transform: scale(0.98);
        }
      `}</style>

      <div className={`table-qr-manager ${isAnimating ? 'open' : ''}`}>
        <div className="manager-wrapper">
          <div className="manager-header">
            <img
              src={backIcon}
              alt="뒤로가기"
              className="back-button"
              onClick={handleBackClick}
            />
            <span className="header-title">테이블 QR 연결</span>
          </div>

          <div className="manager-content">
            {isLoading ? (
              <div className="loading-container">
                <div className="loading-spinner" />
                <span>로딩중...</span>
              </div>
            ) : (
              <>
                <div className="summary-bar">
                  총 {tables.length}개 테이블 · 연결된 QR <span className="linked-qr-count">{totalLinkedQrs}개</span>
                </div>

                <div className="table-list">
                  {tables.map((table) => {
                    const qrList = getTableQrList(table);

                    return (
                      <div key={table.table_cd} className="table-card">
                        <div className="table-header">
                          <span className="table-name">{table.table_nm}</span>
                          <span className="table-code">테이블</span>
                        </div>

                        {qrList.length === 0 ? (
                          <div className="no-qr-text">연결된 QR 없음</div>
                        ) : (
                          <div className="qr-list">
                            {qrList.map((qr) => {
                              const displayUrl = `${window.location.origin}/order/${qr.qr_code}`;
                              return (
                                <div key={qr.qr_code} className="qr-item">
                                  <a
                                    className="qr-url existing"
                                    href={displayUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={(e) => e.stopPropagation()}
                                    title={displayUrl}
                                  >
                                    {displayUrl}
                                  </a>
                                  <button
                                    className="qr-action-btn disconnect"
                                    onClick={() => handleDisconnectClick(qr.qr_code, table.table_nm)}
                                  >
                                    연결 해제
                                  </button>
                                </div>
                              );
                            })}
                          </div>
                        )}

                        <button
                          className="scan-button"
                          onClick={() => handleQrScanClick(table)}
                        >
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M9.5 6.5v3h-3v-3h3M11 5H5v6h6V5zm-1.5 9.5v3h-3v-3h3M11 13H5v6h6v-6zm6.5-6.5v3h-3v-3h3M19 5h-6v6h6V5zm-6 8h1.5v1.5H13V13zm1.5 1.5H16V16h-1.5v-1.5zM16 13h1.5v1.5H16V13zm-3 3h1.5v1.5H13V16zm1.5 1.5H16V19h-1.5v-1.5zM16 16h1.5v1.5H16V16zm1.5-1.5H19V16h-1.5v-1.5zm0 3H19V19h-1.5v-1.5zM19 13h-1.5v1.5H19V13z"/>
                          </svg>
                          QR 촬영하여 연결
                        </button>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>

        </div>
      </div>

      {/* 카메라 스캐너 모달 - 전체화면 */}
      {scannerOpen && (
        <div className="scanner-overlay">
          <div className="scanner-header">
            <span className="scanner-title">QR 스캔</span>
            <button className="scanner-close-btn" onClick={closeScanner}>
              닫기
            </button>
          </div>
          <div className="scanner-body">
            <div id="qr-reader" />
          </div>
          <div className="scanner-bottom">
            <div className="scanner-guide">QR 코드를 화면 안에 맞춰주세요</div>
            {selectedTable && (
              <div className="scanner-table-info">
                {selectedTable.table_nm}
              </div>
            )}
          </div>
        </div>
      )}

      {/* 토스트 메시지 */}
      {toast.show && (
        <div className="toast-popup">
          {toast.type === 'success' && (
            <svg width="16" height="12" viewBox="0 0 16 12" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: '6px' }}>
              <path d="M5.75416 8.8875L13.5229 1.11875C13.7062 0.935417 13.9201 0.84375 14.1646 0.84375C14.409 0.84375 14.6229 0.935417 14.8062 1.11875C14.9896 1.30208 15.0812 1.51994 15.0812 1.77233C15.0812 2.02472 14.9896 2.24228 14.8062 2.425L6.39583 10.8583C6.21249 11.0417 5.9986 11.1333 5.75416 11.1333C5.50971 11.1333 5.29583 11.0417 5.11249 10.8583L1.17083 6.91667C0.987492 6.73333 0.899492 6.51578 0.906826 6.264C0.914159 6.01222 1.0098 5.79436 1.19374 5.61042C1.37769 5.42647 1.59555 5.33481 1.84733 5.33542C2.10033 5.33603 2.32694 5.42958 2.51458 5.61625L5.75416 8.8875Z" fill="#FFD700"/>
            </svg>
          )}
          {toast.type === 'error' && (
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: '6px' }}>
              <path d="M1.93899 0.301937C1.72672 0.103217 1.44597 -0.00496705 1.15587 0.000175271C0.865778 0.0053176 0.588994 0.123385 0.383834 0.329505C0.178674 0.535624 0.0611564 0.813702 0.056038 1.10515C0.0509196 1.39661 0.1586 1.67868 0.356395 1.89194L4.4174 5.97194L0.356395 10.0519C0.24638 10.1549 0.158139 10.2791 0.0969375 10.4171C0.0357358 10.5551 0.00282704 10.7041 0.000174267 10.8552C-0.0024785 11.0062 0.0251791 11.1563 0.0814972 11.2963C0.137815 11.4364 0.22164 11.5637 0.327971 11.6705C0.434302 11.7773 0.56096 11.8615 0.70039 11.9181C0.83982 11.9747 0.989166 12.0025 1.13952 11.9998C1.28987 11.9972 1.43814 11.9641 1.5755 11.9026C1.71286 11.8411 1.83648 11.7525 1.93899 11.6419L6 7.56194L10.061 11.6419C10.1635 11.7525 10.2871 11.8411 10.4245 11.9026C10.5619 11.9641 10.7101 11.9972 10.8605 11.9998C11.0108 12.0025 11.1602 11.9747 11.2996 11.9181C11.439 11.8615 11.5657 11.7773 11.672 11.6705C11.7784 11.5637 11.8622 11.4364 11.9185 11.2963C11.9748 11.1563 12.0025 11.0062 11.9998 10.8552C11.9972 10.7041 11.9643 10.5551 11.9031 10.4171C11.8419 10.2791 11.7536 10.1549 11.6436 10.0519L7.5826 5.97194L11.6436 1.89194C11.8414 1.67868 11.9491 1.39661 11.944 1.10515C11.9388 0.813702 11.8213 0.535624 11.6162 0.329505C11.411 0.123385 11.1342 0.0053176 10.8441 0.000175271C10.554 -0.00496705 10.2733 0.103217 10.061 0.301937L6 4.38194L1.93899 0.301937Z" fill="#EC5A5A"/>
            </svg>
          )}
          {toast.message}
        </div>
      )}

      {/* 확인 다이얼로그 */}
      {confirmDialog.show && (
        <div className="dialog-overlay" onClick={() => setConfirmDialog({ show: false, qrCode: '', tableName: '' })}>
          <div className="dialog-content" onClick={(e) => e.stopPropagation()}>
            <div className="dialog-title">이 QR 코드를 해지할까요?</div>
            <div className="dialog-qr">{confirmDialog.qrCode}</div>
            <div className="dialog-desc">
              해지하면 이 QR로 주문 페이지에 접속할 수 없어요
            </div>
            <div className="dialog-buttons">
              <button
                className="dialog-btn cancel"
                onClick={() => setConfirmDialog({ show: false, qrCode: '', tableName: '' })}
              >
                취소
              </button>
              <button className="dialog-btn confirm" onClick={confirmDisconnect}>
                해지
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 카메라 권한 거부 안내 */}
      {permissionDenied && (
        <div className="permission-overlay" onClick={() => setPermissionDenied(false)}>
          <div className="permission-content" onClick={(e) => e.stopPropagation()}>
            <div className="permission-icon">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="#FF9800">
                <path d="M18 10.48V6c0-1.1-.9-2-2-2H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2v-4.48l4 3.98v-11l-4 3.98zm-2-.79V18H4V6h12v3.69zM10 8H5.09C6.47 10.64 8.58 12 10 12c1.42 0 3.53-1.36 4.91-4H10z"/>
                <path d="M2.5 2.5L21.5 21.5" stroke="#FF9800" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </div>
            <div className="permission-title">카메라 권한이 필요합니다</div>
            <div className="permission-desc">
              QR 스캔을 위해 카메라 권한을 허용해주세요.
            </div>
            <div className="permission-steps">
              <div className="permission-step">1. 휴대폰 <strong>설정</strong> 앱 열기</div>
              <div className="permission-step">2. <strong>앱</strong> {'>'} 해당 앱 선택 {'>'} <strong>권한</strong></div>
              <div className="permission-step">3. <strong>카메라</strong> 항목을 <strong>허용</strong>으로 변경</div>
              <div className="permission-step">4. 앱으로 돌아와 다시 시도</div>
            </div>
            <button className="permission-btn" onClick={() => setPermissionDenied(false)}>
              확인
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default TableQrManager;
